import React, { PureComponent } from "react";
import { get, noop, isNumber } from "lodash";
import { formatRaceDate } from "@tvg/formatter/dates";
import { formatPurse } from "@tvg/formatter/currency";
import { PastPerformanceRace } from "@tvg/ts-types/Race";
import type { Entry } from "@tvg/ts-types/ListItem";
import { NullaryFn } from "@tvg/ts-types/Functional";

import { Icon } from "@tvg/design-system/web";
import PositionInfo from "../PositionInfo";
import {
  PPCard,
  PPCardHeader,
  PPCardOptions,
  Date,
  RaceInfo,
  TitleWrapper,
  Title,
  TitleComplement,
  RaceDataList,
  RaceDataListItem,
  RaceReport,
  IconWrapper,
  FavoriteTag,
  Separator
} from "./styled-components";

interface Props {
  race: PastPerformanceRace;
  isSelected: boolean;
  onClick: NullaryFn<void>;
  raceInfoSeparator: string;
  postRaceReportShown: boolean;
  distanceHighlighted: boolean;
  surfaceHighlighted: boolean;
}

const RUNNER_FINAL_ODD_KEY = "runnerFinalOdd";
const DISTANCE_KEY = "distance";
const SURFACE_KEY = "surface";
const CONDITION_KEY = "condition";
const CLASS_KEY = "class";
const PURSE_KEY = "purse";

export default class PastPerformance extends PureComponent<Props> {
  static defaultProps = {
    onClick: noop,
    isSelected: false,
    raceInfoSeparator: ", ",
    postRaceReportShown: true,
    distanceHighlighted: false,
    surfaceHighlighted: false
  };

  getRunnerFinalOdd() {
    const { odds } = this.props.race;
    if (this.props.race.scratched || !odds || !isNumber(odds.numerator))
      return "";
    return `(${odds.numerator}/${
      isNumber(odds.denominator) ? odds.denominator : 1
    })`;
  }

  raceInfoItems: Entry[] = [
    {
      key: RUNNER_FINAL_ODD_KEY,
      value: this.getRunnerFinalOdd(),
      isHighlighted: false
    },
    {
      key: DISTANCE_KEY,
      value: get(this.props.race, "distance"),
      isHighlighted: this.props.distanceHighlighted
    },
    {
      key: SURFACE_KEY,
      value: get(this.props.race, "surface.shortName"),
      isHighlighted: this.props.surfaceHighlighted
    },
    {
      key: CONDITION_KEY,
      value: get(this.props.race, "surface.defaultCondition"),
      isHighlighted: false
    },
    {
      key: CLASS_KEY,
      value: get(this.props.race, "raceClass.shortName"),
      isHighlighted: false
    },
    {
      key: PURSE_KEY,
      value: formatPurse(get(this.props.race, "purse")),
      isHighlighted: false
    }
  ];

  renderDate = () => (
    <Date data-qa-label="race-info-date">
      {formatRaceDate(this.props.race.raceDate || this.props.race.date)}
    </Date>
  );

  renderPosition = () => (
    <PositionInfo
      finishPosition={this.props.race.finishPosition}
      finishStatus={this.props.race.finishStatus}
      numRunners={this.props.race.numRunners}
      accBeatenDistance={{
        accBeatenDistance: get(this.props.race, "accBeatenDistance", ""),
        accBeatenDistanceStatus: get(
          this.props.race,
          "accBeatenDistanceStatus",
          ""
        )
      }}
      winningDistance={{
        winningDistance: get(this.props.race, "winningDistance", ""),
        winningDistanceStatus: get(this.props.race, "winningDistanceStatus", "")
      }}
    />
  );

  renderReplay = () => (
    <IconWrapper>
      {this.props.race.hasReplays ? (
        <Icon name="replay" lineColor="content.info" size="s" />
      ) : (
        <Icon name="noVideo" lineColor="content.subtle" size="s" />
      )}
    </IconWrapper>
  );

  // eslint-disable-next-line class-methods-use-this
  renderResultNavigation = () => (
    <IconWrapper>
      <Icon
        name="chevronRight"
        lineColor="component.button.tertiary.content.base"
        size="s"
      />
    </IconWrapper>
  );

  renderInfoItem = (entry: Entry, hasSeparator: boolean, isFavorite: boolean) =>
    entry.value &&
    (isFavorite ? (
      <RaceDataListItem
        key={entry.key}
        data-qa-label={`race-info-${entry.key}`}
      >
        {entry.value}
        {isFavorite && <FavoriteTag>FAV</FavoriteTag>}
        {hasSeparator && <Separator>{this.props.raceInfoSeparator}</Separator>}
      </RaceDataListItem>
    ) : (
      <RaceDataListItem
        key={entry.key}
        data-qa-label={`race-info-${entry.key}`}
        isHighlighted={entry.isHighlighted && this.props.postRaceReportShown}
      >
        {entry.value}
        {hasSeparator && <Separator>{this.props.raceInfoSeparator}</Separator>}
      </RaceDataListItem>
    ));

  renderInfo = () => {
    const { favorite } = this.props.race;
    return (
      <RaceInfo>
        <TitleWrapper>
          <Title data-qa-label="race-track-name">
            {this.props.race.track.name}
          </Title>
          <TitleComplement data-qa-label="race-number">
            &nbsp;R{this.props.race.number}
          </TitleComplement>
        </TitleWrapper>
        <RaceDataList>
          {this.raceInfoItems.map((infoItem, infoIndex) =>
            this.renderInfoItem(
              infoItem,
              infoIndex < this.raceInfoItems.length - 1,
              infoIndex === 0 && !!favorite
            )
          )}
        </RaceDataList>
        {this.props.postRaceReportShown && this.props.race.postRaceReport && (
          <RaceReport>{`"${this.props.race.postRaceReport.replace(
            /,(?=[^\s])/g,
            " | "
          )}"`}</RaceReport>
        )}
      </RaceInfo>
    );
  };

  render() {
    return (
      <PPCard
        onClick={!this.props.isSelected ? this.props.onClick : noop}
        isSelected={this.props.isSelected}
      >
        <PPCardHeader>
          {this.renderPosition()}
          <PPCardOptions>
            {this.renderDate()}
            {this.renderReplay()}
            {this.renderResultNavigation()}
          </PPCardOptions>
        </PPCardHeader>
        {this.renderInfo()}
      </PPCard>
    );
  }
}
